/**
 * T145 — /expenses/new.
 *
 * Thin wrapper around the shared ExpenseForm component. Form state + item
 * editing + private-use override live in the component so that /expenses/$id/edit
 * (T146) can share them identically.
 */
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { ExpenseForm, type ExpenseFormSubmit } from '../components/expenses/ExpenseForm';
import {
  DuplicateWarning,
  parseDuplicateError,
  type DuplicateInfo,
} from '../components/expenses/DuplicateWarning';
import { useCreateExpense } from '../services/api/expenses';
import { useToastHelpers } from '../components/ui/Toast';
import { PageHeader } from '../components/ui';
import type { CreateExpenseInput } from '@armard/shared';
import { markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';
import { useClosedQuarterConfirmation } from '../hooks/useClosedQuarterConfirmation';

export const Route = createFileRoute('/expenses_/new')({
  component: NewExpensePage,
});

function NewExpensePage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <NewExpenseInner />
      </AppShell>
    </ProtectedRoute>
  );
}

function NewExpenseInner() {
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const createExpense = useCreateExpense();
  const confirmation = useClosedQuarterConfirmation();
  const [duplicateInfo, setDuplicateInfo] = useState<DuplicateInfo | null>(null);
  const [pendingPayload, setPendingPayload] = useState<CreateExpenseInput | null>(null);

  async function doCreate(
    payload: CreateExpenseInput,
    { ignoreDuplicate }: { ignoreDuplicate: boolean }
  ) {
    try {
      const created = await confirmation.run((confirmed) =>
        createExpense.mutateAsync({
          input: { ...payload, confirmNachträglich: confirmed },
          ignoreDuplicate,
        })
      );
      if (!created) return;
      markFormSaved();
      toast.success(
        created.status === 'draft' ? 'Beleg als Entwurf gespeichert' : 'Beleg angelegt'
      );
      setDuplicateInfo(null);
      setPendingPayload(null);
      navigate({ to: '/expenses/$id', params: { id: created.id } });
    } catch (err) {
      const dup = parseDuplicateError(err);
      if (dup && !ignoreDuplicate) {
        setDuplicateInfo(dup);
        setPendingPayload(payload);
        return;
      }
      toast.error(apiErrorMessage(err));
      setDuplicateInfo(null);
      setPendingPayload(null);
    }
  }

  async function onSubmit(values: ExpenseFormSubmit) {
    const payload: CreateExpenseInput = {
      ...values,
      type: 'invoice',
      referencesAdvanceIds: [],
      // Spec 004 codex Wave-B #2 — invoiceDate is optional on the
      // shared submit type for edit-of-null cases. Create form has
      // `required` on the date input, so this is always a string at
      // runtime; coerce to satisfy the strict create schema (`?? ''`
      // would hit the API 400 if it ever reached here without a date).
      invoiceDate: values.invoiceDate ?? '',
      dueDate: values.dueDate,
      currency: 'EUR',
      reverseCharge: values.reverseCharge,
      supplierKleinunternehmer: values.supplierKleinunternehmer,
      isKleinbetragsrechnung: values.isKleinbetragsrechnung,
      privateUseOverridePercent: values.privateUseOverridePercent,
      privateUseReason: values.privateUseReason,
      notes: values.notes,
      items: values.items,
      confirmNachträglich: false,
      asDraft: false,
    };
    await doCreate(payload, { ignoreDuplicate: false });
  }

  return (
    <>
      {/* 006b P1: Header gehört der Route (PATTERNS.md §3) */}
      <PageHeader backTo="/expenses" backLabel="Ausgaben" title="Neuer Beleg" />
      {confirmation.dialog}
      <ExpenseForm
        mode="create"
        onSubmit={onSubmit}
        submitLabel="Speichern"
        submitting={createExpense.isPending}
        cancelHref="/expenses"
      />
      <DuplicateWarning
        info={duplicateInfo}
        submitting={createExpense.isPending}
        onForceSave={() => {
          if (pendingPayload) {
            void doCreate(pendingPayload, { ignoreDuplicate: true });
          }
        }}
        onCancel={() => {
          setDuplicateInfo(null);
          setPendingPayload(null);
        }}
      />
    </>
  );
}
